import React, { useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { TiTick } from 'react-icons/ti';

interface IMultiSelectField {
  type: string;
  options: string[];
  selectedOptions: string[];
  setSelectedOptions: (arg) => unknown;
}

const MultiSelectInput = ({
  type,
  options,
  selectedOptions,
  setSelectedOptions,
}: IMultiSelectField) => {
  const [open, setOpen] = useState<boolean>(false);

  const isSelected = (option: string) => selectedOptions.includes(option);

  const toggleOption = (option: string) => {
    if (isSelected(option)) {
      setSelectedOptions(
        selectedOptions.filter((selected) => selected !== option)
      );
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }

    setOpen(false);
  };

  return (
    <div className="flex">
      <div className="w-full">
        <Listbox
          as="div"
          className="space-y-1"
          value={selectedOptions}
          onChange={(value) => toggleOption(value as unknown as string)}
        >
          {() => (
            <div className="relative">
              <span className="inline-block w-full rounded-md shadow-sm">
                <Listbox.Button
                  className="cursor-default  relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                  onClick={() => setOpen(!open)}
                >
                  <span className="block truncate">
                    {selectedOptions.length < 1
                      ? `Select ${type}`
                      : `Selected ${type} (${selectedOptions.length}) `}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Listbox.Button>
              </span>

              <Transition
                unmount={false}
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="mt-1 w-full rounded-md bg-white shadow-lg "
              >
                <Listbox.Options
                  static
                  className="rounded-md py-1 text-base leading-6 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm w-full max-h-[200px] overflow-y-auto"
                >
                  {options.map((option) => {
                    const selected = isSelected(option);
                    return (
                      <Listbox.Option key={option} value={option}>
                        {({ active }) => (
                          <div
                            className={`${
                              active ? 'text-white' : 'text-gray-900'
                            } cursor-pointer flex items-center group select-none py-2 pl-2  pr-4 hover:bg-blue-600 hover:text-white`}
                          >
                            {selected && (
                              <TiTick
                                className={`${
                                  active ? 'text-white' : 'text-blue-600'
                                }  flex items-center h-6 w-6 group-hover:text-white`}
                              />
                            )}
                            <p
                              className={`${
                                selected ? 'font-semibold' : 'font-normal'
                              } block truncate pl-2`}
                            >
                              {option}
                            </p>
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
      </div>
    </div>
  );
};

export default MultiSelectInput;
